import { Component, HostListener, Input, OnDestroy, OnInit, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime, share } from 'rxjs/operators';

import { GroupDropDownItem } from '../../models/group-drop-down';

export const CUSTOM_GROUP_DROP_DOWN_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => GroupDropDownComponent),
    multi: true,
};

@Component({
    selector: 'bcu-group-drop-down',
    templateUrl: './group-drop-down.component.html',
    styleUrls: ['./group-drop-down.component.scss'],
    providers: [CUSTOM_GROUP_DROP_DOWN_CONTROL_VALUE_ACCESSOR],
    standalone: false
})
export class GroupDropDownComponent implements OnInit, OnDestroy, ControlValueAccessor {
    @Input() public isDisabled = false;
    @Input() public multiple = false;
    @Input() public textAlign: 'center' | 'left' | 'right' = 'center';
    @Input() public sortItems = false;
    @Input() public wasToggledWithLastClick = false;
    @Input() public placeHolder = 'Select';
    @Input() public displayKey = 'name';
    @Input() public valueKey = 'code';
    @Input() public groupByKey = 'group';
    @Input() public maxHeigth = 0;
    @Input() public dropDownWidth = 0;
    @Input() public displayGroupFunc: (group: string) => string = null;

    public searchInput = new UntypedFormControl();

    private _options: any[] = [];

    @Input()
    public set options(values: any) {
        this.setOptions(values);
    }
    public get options(): any {
        return this._options;
    }

    public items: GroupDropDownItem[];
    public filteredItems: GroupDropDownItem[];

    public selectedDisplayText: string = '';
    public toggleDropDown = false;

    public selectedItems: any[] = [];

    public clickedInside = false;

    private subscriptions: Subscription = new Subscription();

    onChange = (values: any) => {};
    onTouched = () => {};

    constructor() {}

    ngOnInit() {
        this.subscribeToSearchInputChanges();
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    public toggleSelectDropDown($event: Event) {
        if (this.isDisabled) {
            return;
        }

        this.wasToggledWithLastClick = true;

        this.toggleDropDown = !this.toggleDropDown;
    }

    private setOptions(items: any[]) {
        this._options = items;
        this.items = [];
        this.filteredItems = [];
        const groups = [...new Set(this._options.map(option => option[this.groupByKey]))];
        groups.forEach((item, index) => {
            const groupItem = new GroupDropDownItem();
            groupItem.displayName = this.displayGroupFunc ? this.displayGroupFunc(item) : item;
            groupItem.expanded = false;
            groupItem.selected = false;
            groupItem.groupItem = true;
            groupItem.value = 'bcGroup' + item.toLowerCase();
            const groupItems = this._options
                .filter(option => option[this.groupByKey] === item)
                .map(option => {
                    const dropDownItem = new GroupDropDownItem();
                    dropDownItem.displayName = option[this.displayKey];
                    dropDownItem.value = option[this.valueKey];
                    dropDownItem.item = option;
                    dropDownItem.groupItem = false;
                    dropDownItem.parent = groupItem;
                    return dropDownItem;
                });
            groupItem.items = [];
            groupItem.items.push(...groupItems);
            this.items.push(groupItem);
            this.items.push(...groupItems);
            this.filteredItems.push(groupItem);
            this.filteredItems.push(...groupItems);
        });
    }

    public getDropDownStyle() {
        const style = {
            'max-height.px': this.getMaxHeigth(),
        };
        if (this.dropDownWidth > 0) {
            style['width.px'] = this.dropDownWidth;
        }
        return style;
    }

    public getInputClass() {
        return this.toggleDropDown ? 'input-focus' : '';
    }

    public getArrowClass() {
        return this.toggleDropDown ? 'arrow-down-focus' : '';
    }

    public getMaxHeigth() {
        if (this.maxHeigth > 0) {
            return this.maxHeigth;
        }

        return 1080;
    }

    itemClick(item: GroupDropDownItem) {
        if (!this.multiple && item.groupItem) {
            return;
        }

        if (this.multiple) {
            if (item.groupItem) {
                this.setSelectedForGroupItem(item);
            } else {
                this.setItemSelectedState(item);
            }
            this.setGroupItemsSelectState();
            this.onChange(this.selectedItems.filter(si => !si.groupItem).map(si => si.item));
            this.setSelectedDisplayText();
        } else {
            this.selectedItems = [item];
            const selectedItem = this.selectedItems.map(si => si.item);
            this.onChange(selectedItem[0]);
            this.toggleDropDown = false;
            this.setSelectedDisplayText();
            this.filterOptions('');
        }
    }

    public isItemSelected(item: GroupDropDownItem): boolean {
        if (item.groupItem && !this.multiple) {
            return false;
        } else {
            return this.selectedItems.findIndex(si => si.value === item.value) !== -1;
        }
    }

    public get noItems(): boolean {
        return this.filteredItems.length === 0;
    }

    private subscribeToSearchInputChanges() {
        const searchInputChanged = this.searchInput.valueChanges.pipe(share());

        this.subscriptions.add(
            searchInputChanged.subscribe(searchPhrase => {
                this.clearSelectedOptionIfDifferentThanTextInput(searchPhrase);
            })
        );

        this.subscriptions.add(
            searchInputChanged.pipe(debounceTime(300)).subscribe(phrase => {
                this.filterOptions(phrase);
            })
        );
    }

    private setSelectedDisplayText() {
        this.selectedDisplayText = this.selectedItems.map(x => x.displayName).join(',');
        this.searchInput.setValue(this.selectedDisplayText, { emitEvent: false });
    }
    private clearSelectedDisplayText() {
        this.selectedDisplayText = '';
        this.searchInput.setValue('', { emitEvent: false });
    }

    private filterOptions(searchPhrase: string) {
        if (!searchPhrase) {
            this.filteredItems = [...this.items];
            return;
        }
        this.filteredItems = [
            ...this.items.filter(item => {
                if (item.groupItem) {
                    const filteredItems = item.items.filter(childItem => {
                        const childDisplayName = childItem.displayName.toLowerCase();
                        return childDisplayName.includes(searchPhrase.toLowerCase());
                    });
                    return filteredItems.length > 0;
                } else {
                    const lowerCaseDisplayName = item.displayName.toLowerCase();
                    return lowerCaseDisplayName.includes(searchPhrase.toLowerCase());
                }
            }),
        ];
    }

    private clearSelectedOptionIfDifferentThanTextInput(searchPhrase: string) {
        if (this.selectedItems.length === 0) {
            return;
        }
        if (!this.multiple) {
            const selectedItem = this.selectedItems[0];
            if (searchPhrase !== selectedItem.displayName) {
                this.clearSelectedDisplayText();
                this.selectedItems = [];
                this.onChange(null);
            }
        }
    }

    private setGroupItemsSelectState() {
        this.items
            .filter(i => i.groupItem)
            .forEach(groupItem => {
                if (this.areAllItemsFromGroupSelected(groupItem)) {
                    if (!this.isItemSelected(groupItem)) {
                        this.selectItem(groupItem);
                    }
                } else {
                    if (this.isItemSelected(groupItem)) {
                        this.unselectItem(groupItem);
                    }
                }
            });
    }

    private areAllItemsFromGroupSelected(groupItem: GroupDropDownItem): boolean {
        return groupItem.items.filter(i => this.isItemSelected(i)).length === groupItem.items.length;
    }

    private selectItem(item: GroupDropDownItem) {
        this.selectedItems.push(item);
    }

    private unselectItem(item: GroupDropDownItem) {
        const selectedItem = this.selectedItems.find(x => x.value === item.value);
        this.selectedItems.splice(this.selectedItems.indexOf(selectedItem), 1);
    }

    private setItemSelectedState(item: GroupDropDownItem) {
        if (!this.isItemSelected(item)) {
            this.selectItem(item);
        } else {
            const selectedItem = this.selectedItems.find(x => x.value === item.value);
            this.unselectItem(selectedItem);
        }
    }

    private setSelectedForGroupItem(item: GroupDropDownItem) {
        if (!this.isItemSelected(item)) {
            this.selectedItems.push(item);
            item.items.forEach(ci => {
                if (!this.isItemSelected(ci)) {
                    this.selectedItems.push(ci);
                }
            });
        } else {
            const selectedItem = this.selectedItems.find(x => x.value === item.value);
            this.selectedItems.splice(this.selectedItems.indexOf(selectedItem), 1);
            item.items.forEach(ci => {
                const childSelectedItem = this.selectedItems.find(x => x.value === ci.value);
                this.selectedItems.splice(this.selectedItems.indexOf(childSelectedItem), 1);
            });
        }
    }

    writeValue(obj: any): void {
        this.selectedItems = [];
        if (obj !== null) {
            if (Array.isArray(obj)) {
                obj.forEach(item => {
                    const dItem = this.items.find(di => di.value === item[this.valueKey]);
                    if (dItem) {
                        this.selectedItems.push(dItem);
                    }
                });
                this.setGroupItemsSelectState();
            } else {
                const dItem = this.items.find(di => di.value === obj[this.valueKey]);
                if (dItem) {
                    this.selectedItems.push(dItem);
                }
            }
            this.setSelectedDisplayText();
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }
    setDisabledState?(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    @HostListener('click')
    public clickInsideComponent() {
        this.clickedInside = true;
    }

    @HostListener('document:click')
    public clickOutsideComponent() {
        if (this.wasToggledWithLastClick) {
            this.wasToggledWithLastClick = false;
            this.clickedInside = false;
            return;
        }

        if (!this.clickedInside) {
            this.toggleDropDown = false;
            if (this.selectedItems.length === 0) {
                this.clearSelectedDisplayText();
            }
            this.setSelectedDisplayText();
            this.filterOptions('');
        }
        this.clickedInside = false;
    }
}
